import { getRealtimeBaseUrl, isDev } from '@safe-minds-ai/utils';

export type RealtimeChannel = 'chat' | 'flags' | 'usage';

export interface RealtimeEvent<TPayload = unknown> {
  channel: RealtimeChannel;
  payload: TPayload;
}

export type RealtimeMessageHandler<TPayload = unknown> = (event: RealtimeEvent<TPayload>) => void;

export interface RealtimeConnection {
  close: () => void;
}

export function openEventStream({
  channel,
  token,
  onMessage,
  onError,
  onOpen,
}: {
  channel: RealtimeChannel;
  token?: string | null;
  onMessage: RealtimeMessageHandler;
  onError?: (error: MessageEvent | Event) => void;
  onOpen?: () => void;
}): RealtimeConnection {
  const baseUrl = getRealtimeBaseUrl().replace(/\/$/, '');
  const url = new URL(`${baseUrl}/${channel}`, window.location.origin);
  if (token) {
    url.searchParams.set('access_token', token);
  }

  const eventSource = new EventSource(url.toString(), { withCredentials: true });

  eventSource.addEventListener('message', (event) => {
    try {
      const parsed = JSON.parse(event.data);
      onMessage(parsed);
    } catch (error) {
      if (isDev()) {
        console.warn('Failed to parse realtime payload', error);
      }
    }
  });

  if (onOpen) {
    eventSource.addEventListener('open', onOpen);
  }

  if (onError) {
    eventSource.addEventListener('error', onError);
  }

  return {
    close: () => eventSource.close(),
  };
}

export function openWebSocket({
  channel,
  token,
  onMessage,
}: {
  channel: RealtimeChannel;
  token?: string | null;
  onMessage: RealtimeMessageHandler;
}): RealtimeConnection {
  const base = getRealtimeBaseUrl().replace(/\/$/, '');
  const protocol = typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'wss' : 'ws';
  const host = typeof window !== 'undefined' ? window.location.host : 'localhost:3000';
  const socketUrl = `${protocol}://${host}${base}/${channel}`;
  const url = token ? `${socketUrl}?access_token=${encodeURIComponent(token)}` : socketUrl;
  const socket = new WebSocket(url);

  socket.onmessage = (event) => {
    try {
      const parsed = JSON.parse(event.data);
      onMessage(parsed);
    } catch (error) {
      if (isDev()) {
        console.warn('Failed to parse realtime websocket payload', error);
      }
    }
  };

  return {
    close: () => socket.close(),
  };
}
